import { CommonDomainDataRc } from '@learnics/models/src/RunningCalculator/CommonDomainDataRc.mjs'
import { CommonSearchesRc } from '@learnics/models/src/RunningCalculator/CommonSearchesRc.mjs'
import { CommonSearchWordFrequenciesRc } from '@learnics/models/src/RunningCalculator/CommonSearchWordFrequenciesRc.mjs'
import { CommonUrlCategoriesRc } from '@learnics/models/src/RunningCalculator/CommonUrlCategoriesRc.mjs'
import { CommonUrlDataRc } from '@learnics/models/src/RunningCalculator/CommonUrlDataRc.mjs'

import Vue from 'vue'
import LoggerService from '@learnics/services/src/logger'

export const state = {
  events: {},
  eventMetaInfos: {},
  secrets: {},
  keysDeleted: {},
  keysBlacklisted: {},
  loggerSessions: {},
  runningCalculators: {},
  commonRunningCalculators: {},
  currentSessionId: null,
  loadingAnyEventData: 0,
  loadingEventLogData: {},
  loadedLoggerSessionsByUser: {},
  loadingLoggerSessionsByUser: {},
}

export const getters = {
  getEvents: (state) => state.events,
  getEventMetaInfos: (state) => state.eventMetaInfos,
  getSecrets: (state) => state.secrets,
  getKeysDeleted: (state) => state.keysDeleted,
  getKeysBlacklisted: (state) => state.keysBlacklisted,
  getLoggerSessions: (state) => state.loggerSessions,
  getRunningCalculators: (state) => state.runningCalculators,
  getCommonRunningCalculators: (state) => state.commonRunningCalculators,
  getLoadingAnyEventData: (state) => state.loadingAnyEventData,
  getLoadingEventLogData: (state) => state.loadingEventLogData,
  currentSessionId: (state) => state.currentSessionId,
  currentSession: (state, getters) => {
    return state.loggerSessions[state.currentSessionId] || null
  },

  loadedLoggerSessionsByUser: (state) => state.loadedLoggerSessionsByUser,
  loadingLoggerSessionsByUser: (state) => state.loadingLoggerSessionsByUser,
}

export const mutations = {
  setLoadedLoggerSessionsByUser(state, { userId, value }) {
    if (value) {
      Vue.set(state.loadedLoggerSessionsByUser, userId, true)
    } else {
      Vue.delete(state.loadedLoggerSessionsByUser, userId)
    }
  },
  setLoadingLoggerSessionsByUser(state, { userId, value }) {
    if (value) {
      Vue.set(state.loadingLoggerSessionsByUser, userId, true)
    } else {
      Vue.delete(state.loadingLoggerSessionsByUser, userId)
    }
  },
  updateLoadingAnyEventData(state, payload) {
    if (!payload) throw new Error('Payload is null')
    const { loading = false } = payload
    state.loadingAnyEventData = loading
      ? state.loadingAnyEventData + 1
      : state.loadingAnyEventData - 1
  },
  setCurrentSessionId(state, payload) {
    Vue.set(state, 'currentSessionId', payload || null)
  },
  addRunningCalculator(state, payload) {
    if (!payload) throw new Error('Payload is null')
    if (!payload.id) throw new Error('Payload.id is null')
    if (!payload.parentId) throw new Error('Payload.parentId is null')
    const target = payload.common
      ? 'commonRunningCalculators'
      : 'runningCalculators'

    if (!payload.calculator && state[target][payload.parentId]) {
      Vue.delete(state[target][payload.parentId], payload.id)
      if (Object.keys(state[target][payload.parentId]).length === 0) {
        Vue.delete(state[target], payload.parentId)
      }
    } else if (payload.calculator) {
      if (!state[target][payload.parentId]) {
        Vue.set(state[target], payload.parentId, {})
      }
      Vue.set(state[target][payload.parentId], payload.id, payload.calculator)
    }
  },
  addRunningCalculators(state, payload) {
    if (!payload) throw new Error('Payload is null')
    if (!payload.calculators) throw new Error('Payload.calculators is null')
    if (!payload.sessionId)
      throw new Error(
        'Payload.sessionId is null.  A session ID is required to add running calculators.',
      )
    const target = payload.common
      ? 'commonRunningCalculators'
      : 'runningCalculators'
    const parentId = payload.common ? payload.assignmentId : payload.sessionId
    Vue.set(state[target], parentId, payload.calculators)
  },
  addLoggerSession(state, payload) {
    if (!payload) throw new Error('Payload is null')
    Vue.set(state.loggerSessions, payload.id, payload)
  },
  addEvents(state, payload) {
    if (!payload) throw new Error('Payload is null')
    if (!payload.events) throw new Error('Payload.events is null')
    if (!payload.sessionId) throw new Error('Payload.sessionId is null')
    Vue.set(state.events, payload.sessionId, Object.freeze(payload.events))
  },
  addSecrets(state, payload) {
    if (!payload) throw new Error('Payload is null')
    if (!payload.sessionId) throw new Error('Payload.sessionId is null')
    if (!payload.secrets) {
      Vue.delete(state.secrets, payload.sessionId)
    } else {
      Vue.set(state.secrets, payload.sessionId, Object.freeze(payload.secrets))
    }
  },
  addKeysDeleted(state, payload) {
    if (!payload) throw new Error('Payload is null')
    if (!payload.keysDeleted) throw new Error('Payload.keysDeleted is null')
    if (!payload.sessionId) throw new Error('Payload.sessionId is null')
    Vue.set(
      state.keysDeleted,
      payload.sessionId,
      Object.freeze(payload.keysDeleted),
    )
  },

  addKeysBlacklisted(state, payload) {
    if (!payload) throw new Error('Payload is null')
    if (!payload.keysBlacklisted)
      throw new Error('Payload.keysBlacklisted is null')
    if (!payload.sessionId) throw new Error('Payload.sessionId is null')
    Vue.set(
      state.keysBlacklisted,
      payload.sessionId,
      Object.freeze(payload.keysBlacklisted),
    )
  },

  addEventMetaInfo(state, payload) {
    if (!payload) throw new Error('Payload is null')
    if (!payload.eventMetaInfo) throw new Error('Payload.eventMetaInfo is null')
    if (!payload.sessionId) throw new Error('Payload.sessionId is null')

    Vue.set(
      state.eventMetaInfos,
      payload.sessionId,
      Object.freeze(payload.eventMetaInfo),
    )
  },
  updateEventLogDataLoading(state, payload) {
    if (!payload) throw new Error('Payload is null')
    if (!payload.sessionId) throw new Error('Payload.sessionId is null')
    const currentVal = state.loadingEventLogData[payload.sessionId] || 0
    Vue.set(
      state.loadingEventLogData,
      payload.sessionId,
      currentVal + (payload.value ? 1 : -1),
    )
  },
}

export const actions = {
  async loadPlainEventMetaInfo(
    { state, commit, dispatch, rootState },
    args = { forced: false },
  ) {
    const sessionId = args.sessionId
    if (!sessionId) throw new Error('No sessionId provided')

    if (!args.forced && state.eventMetaInfos[sessionId]) return
    try {
      let eventMetaInfo = await LoggerService.getEventMetaInfo(sessionId)
      let session = await LoggerService.getLoggerSession(sessionId)
      if (!session) {
        throw new Error('No session found for sessionId: ' + sessionId)
      }
      commit('addLoggerSession', session)
      commit('addEventMetaInfo', {
        sessionId,
        eventMetaInfo,
      })
    } catch (error) {
      console.log(
        'There was an error loading the eventMetaInfo/loggerSession: ' + error,
      )
      throw error
    }
  },

  async loadPlainEventLogSubmissionData(
    { state, commit, dispatch, rootState },
    args = { forced: false },
  ) {
    const { sessionId, timeRange } = args

    await dispatch('loadPlainEventMetaInfo', {
      sessionId,
    })

    const metaInfo = state.eventMetaInfos[sessionId]
    if (!metaInfo) throw new Error('No metaInfo found for sessionId.')
    if (!args.forced && state.events[sessionId]) return

    try {
      // Determine the user id

      const { events, secrets, keysDeleted, keysBlacklisted, calculators } =
        await LoggerService.loadRunningCalculators(
          sessionId,
          [],
          [
            'submissionsRc',
            'urlCategoriesRc',
            'urlTimelinesRc',
            'domainDataRc',
            'searchesRc',
            'searchWordFrequenciesRc',
          ],
          null,
          {},
          { timeRange },
        )
      if (events) {
        commit('addEvents', {
          sessionId,
          events,
        })
      }
      commit('addRunningCalculators', {
        sessionId,
        calculators: { ...calculators },
        common: false,
      })
      commit('addSecrets', {
        sessionId,
        secrets,
      })
      commit('addKeysDeleted', {
        sessionId,
        keysDeleted,
      })
      commit('addKeysBlacklisted', {
        sessionId,
        keysBlacklisted,
      })
    } catch (error) {
      console.log('There was an error loading the eventMetaInfo: ' + error)
      throw error
    }
  },

  async loadEventLogSubmissionData(
    { state, commit, dispatch, rootState },
    args = { forced: false },
  ) {
    if (!args) throw new Error('No args provided')
    const { sessionId, assignmentId, timeRange } = args
    if (!sessionId) throw new Error('No sessionId provided')
    if (!assignmentId) throw new Error('No assignmentId provided')

    commit('updateEventLogDataLoading', {
      sessionId,
      value: true,
    })
    await dispatch('loadPlainEventMetaInfo', {
      sessionId,
      assignmentId,
    })

    const metaInfo = state.eventMetaInfos[sessionId]
    if (!metaInfo) throw new Error('No metaInfo found for sessionId.')
    const assignment = rootState.assignments.assignments[assignmentId]
    if (!assignment) throw new Error('Assignment not found. Load it first.')

    let commonCalculators = state.commonRunningCalculators[assignmentId]
    if (!commonCalculators) {
      console.log(
        'No common calculators found for assignmentId: ' +
          assignmentId +
          '.  Creating them.',
      )
      commonCalculators = {
        commonDomainDataRc: new CommonDomainDataRc({}),
        commonSearchesRc: new CommonSearchesRc({}),
        commonSearchWordFrequenciesRc: new CommonSearchWordFrequenciesRc({}),
        commonUrlDataRc: new CommonUrlDataRc({}),
        commonUrlCategoriesRc: new CommonUrlCategoriesRc({}),
      }

      commit('addRunningCalculators', {
        sessionId,
        assignmentId,
        calculators: { ...commonCalculators },
        common: true,
      })
    }
    const assignmentDetail =
      rootState.assignments.assignmentDetails[assignment.assignmentDetailId]
    if (!assignmentDetail)
      throw new Error('Assignment detail not found.  Load it first.')

    if (
      !args.forced &&
      state.loadingEventLogData[sessionId] < 2 &&
      state.events[sessionId]
    ) {
      commit('updateEventLogDataLoading', {
        sessionId,
        value: false,
      })
      return
    }

    try {
      // Determine the user id
      commit('updateLoadingAnyEventData', { loading: true })

      const uids = Object.keys(
        rootState.assignments.assignmentKeys[assignmentId] || {},
      )
      const foundUid = uids.find(
        (uid) =>
          rootState.assignments.assignmentKeys[assignmentId][uid] === sessionId,
      )
      if (state.runningCalculators[sessionId]) {
        // We're setting a new set of running calculators for a session.  Remove the old ones
        // from the common calculations.
        const commonCalcIds = Object.keys(
          state.commonRunningCalculators[assignmentId] || {},
        )
        for (let i = 0; i < commonCalcIds.length; i++) {
          state.commonRunningCalculators[assignmentId][
            commonCalcIds[i]
          ].addSession(
            state.runningCalculators[sessionId],
            state.secrets[sessionId],
            -1,
          )
        }
      }
      const { events, secrets, keysDeleted, keysBlacklisted, calculators } =
        await LoggerService.getBackwardCompatibleRunningCalculators(
          sessionId,
          assignment,
          assignmentDetail,
          foundUid,
          metaInfo,
          [
            'submissionsRc',
            'urlCategoriesRc',
            'urlTimelinesRc',
            'domainDataRc',
            'searchesRc',
            'searchWordFrequenciesRc',
          ],
          commonCalculators,
          {
            timeRange,
          },
        )
      if (events) {
        commit('addEvents', {
          sessionId,
          events,
        })
      }
      commit('addRunningCalculators', {
        sessionId,
        assignmentId,
        calculators: { ...calculators },
        common: false,
      })
      commit('addRunningCalculators', {
        sessionId,
        assignmentId,
        calculators: { ...commonCalculators },
        common: true,
      })
      commit('addSecrets', {
        sessionId,
        secrets,
      })
      commit('addKeysDeleted', {
        sessionId,
        keysDeleted,
      })
      commit('addKeysBlacklisted', {
        sessionId,
        keysBlacklisted,
      })
    } catch (error) {
      console.error('There was an error loading the eventMetaInfo: ' + error)
      commit('updateEventLogDataLoading', {
        sessionId,
        value: false,
      })
      commit('updateLoadingAnyEventData', { loading: false })

      throw error
    }

    commit('updateEventLogDataLoading', {
      sessionId,
      value: false,
    })
    commit('updateLoadingAnyEventData', { loading: false })
  },

  async loadLoggerSession({ commit, state }, args = { forced: false }) {
    if (!args) throw new Error('No args provided')
    const { sessionId } = args
    if (!sessionId) throw new Error('No sessionId provided')
    if (state.loggerSessions[sessionId] && !args.forced) {
      return
    }
    commit('updateLoadingAnyEventData', { loading: true })
    try {
      const session = await LoggerService.getLoggerSession(sessionId)
      if (session) {
        commit('addLoggerSession', session)
      }
    } catch (error) {
      console.error('There was an error loading the logger session: ' + error)
    }
    commit('updateLoadingAnyEventData', { loading: false })
  },

  async loadLoggerSessionsByUserId(
    { commit, state },
    args = { forced: false },
  ) {
    if (!args) throw new Error('No args provided')
    const { userId } = args
    if (!userId) throw new Error('No userId provided')
    if (
      (state.loadedLoggerSessionsByUser[userId] ||
        state.loadingLoggerSessionsByUser[userId]) &&
      !args.forced
    ) {
      return
    }
    commit('setLoadingLoggerSessionsByUser', { userId, value: true })
    commit('updateLoadingAnyEventData', { loading: true })
    try {
      const sessions = await LoggerService.getLoggerSessionsForUser(userId)
      const sessionIds = Object.keys(sessions)
      sessionIds.forEach((sessionId) => {
        const session = sessions[sessionId]

        if (session) {
          commit('addLoggerSession', session)
        }
      })
      commit('setLoadedLoggerSessionsByUser', { userId, value: true })
    } catch (error) {
      console.error(
        'There was an error loading the logger sessions by user Id: ' + error,
      )
    } finally {
      commit('setLoadingLoggerSessionsByUser', { userId, value: false })
      commit('updateLoadingAnyEventData', { loading: false })
    }
  },

  async loadLoggerSessionByResearchNotebookId(
    { commit, state },
    args = { forced: false },
  ) {
    if (!args) throw new Error('No args provided')
    const { researchNotebookId, forced } = args
    if (!researchNotebookId) throw new Error('No researchNotebookId provided')
    const sessions = state.loggerSessions
    const existingSessionId = Object.keys(sessions).find(
      (sessionId) =>
        sessions[sessionId].researchNotebookId === researchNotebookId,
    )
    if (!forced && existingSessionId) {
      return
    }
    commit('updateLoadingAnyEventData', { loading: true })
    try {
      const sessions =
        await LoggerService.getLoggerSessionsForResearchNotebook(
          researchNotebookId,
        )
      const sessionIds = Object.keys(sessions)
      sessionIds.forEach((sessionId) => {
        commit('addLoggerSession', sessions[sessionId])
      })
    } catch (error) {
      console.error('There was an error loading the logger session: ' + error)
    }
    commit('updateLoadingAnyEventData', { loading: false })
  },
}
